class PromotionPopup extends HTMLElement {
  constructor() {
    super();

    this.template = this.querySelector('[data-promotion-template]');
    this.modal = null;
    this.closeButtons = this.querySelectorAll('[data-promotion-close]');
    this.actionLinks = this.querySelectorAll('.m-promotion-popup__actions a');

    this.isOpen = false;
    this.timer = null;

    this.handleOverlayClick = this.handleOverlayClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.handleActionLinkClick = this.handleActionLinkClick.bind(this);
    this.handleSectionSelect = this.handleSectionSelect.bind(this);
    this.handleSectionLoad = this.handleSectionLoad.bind(this);
  }

  connectedCallback() {
    if (this.initialized) return;

    this.initialized = true;

    if (window.Shopify && Shopify.designMode) {
      document.addEventListener('shopify:section:select', this.handleSectionSelect);
      document.addEventListener('shopify:section:load', this.handleSectionLoad);

      if (this.dataset.designMode === 'true') {
        this.scheduleOpen(0);
        return;
      }
    }

    if (this.shouldAutoOpen()) {
      this.scheduleOpen(this.getDelay());
    }
  }

  disconnectedCallback() {
    clearTimeout(this.timer);

    this.closeButtons.forEach((button) => button.removeEventListener('click', this.handleClose));
    this.actionLinks.forEach((link) => link.removeEventListener('click', this.handleActionLinkClick));
    this.modal?.close();
    document.removeEventListener('shopify:section:select', this.handleSectionSelect);
    document.removeEventListener('shopify:section:load', this.handleSectionLoad);
    window.removeEventListener('keydown', this.handleKeyDown);
  }

  getDelay() {
    return Math.max(0, Number(this.dataset.delaySeconds || 0)) * 1000;
  }

  getIntervalHours() {
    return Math.max(1, Number(this.dataset.intervalHours || 24));
  }

  isReturningCustomer() {
    const isLoggedIn = this.dataset.customerLoggedIn === 'true';
    const ordersCount = Number(this.dataset.customerOrdersCount || 0);

    return isLoggedIn && ordersCount > 0;
  }

  getStorageKey() {
    return `Minimog:promotion-popup:next-eligible:${this.id}`;
  }

  canUseLocalStorage() {
    try {
      const storageKey = 'minimog:promotion-popup:storage-test';
      window.localStorage.setItem(storageKey, '1');
      window.localStorage.removeItem(storageKey);
      return true;
    } catch (error) {
      return false;
    }
  }

  getNextEligibleAt() {
    if (!this.canUseLocalStorage()) return 0;

    return Number(window.localStorage.getItem(this.getStorageKey()) || 0);
  }

  shouldSuppressForInterval() {
    return this.getNextEligibleAt() > Date.now();
  }

  markNextEligibleAt() {
    if (!this.canUseLocalStorage()) return;

    const nextEligibleAt = Date.now() + this.getIntervalHours() * 60 * 60 * 1000;
    window.localStorage.setItem(this.getStorageKey(), String(nextEligibleAt));
  }

  shouldAutoOpen() {
    if (this.dataset.enable !== 'true') return false;
    if (this.isReturningCustomer()) return false;
    if (this.shouldSuppressForInterval()) return false;

    return true;
  }

  scheduleOpen(delay) {
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.openPopup(), delay);
  }

  hasOtherBlockingUi() {
    const otherOpenModal = Array.from(document.querySelectorAll('.m-modal.m-open-modal')).some((modal) => modal !== this.modal?.modal);

    return (
      otherOpenModal
      || Boolean(document.querySelector('.m-cart-drawer--active'))
      || Boolean(document.querySelector('.m-search-popup[open]'))
    );
  }

  openPopup() {
    if (this.isOpen) return;

    if (this.hasOtherBlockingUi()) {
      this.scheduleOpen(1000);
      return;
    }

    if (!this.template) return;

    if (!this.modal) {
      this.modal = new MinimogTheme.Modal();
      this.modal.modal.classList.add('m-promotion-popup');
      this.modal.modal.addEventListener('click', this.handleOverlayClick);
    }

    const modalRoot = this.modal.modal;
    modalRoot.classList.add('m-promotion-popup');
    modalRoot.classList.toggle('m-promotion-popup--hide-close', this.dataset.showCloseButton !== 'true');
    if (this.dataset.descriptionHighlightColor) {
      modalRoot.style.setProperty('--promotion-highlight-color', this.dataset.descriptionHighlightColor);
    }

    const templateContent = this.template.content.cloneNode(true);
    const panel = templateContent.querySelector('.m-promotion-popup__panel');
    const hasImage = Boolean(templateContent.querySelector('.m-promotion-popup__media'));

    if (panel) {
      panel.classList.toggle('m-promotion-popup--no-image', !hasImage);
    }

    modalRoot.classList.toggle('m-promotion-popup--has-image', hasImage);
    modalRoot.classList.toggle('m-promotion-popup--no-image', !hasImage);
    modalRoot.classList.toggle('m-promotion-popup--image-left', hasImage && this.dataset.imagePosition === 'left');
    modalRoot.classList.toggle('m-promotion-popup--image-right', hasImage && this.dataset.imagePosition !== 'left');

    this.closeButtons = templateContent.querySelectorAll('[data-promotion-close]');
    this.actionLinks = templateContent.querySelectorAll('.m-promotion-popup__actions a');
    this.closeButtons.forEach((button) => button.addEventListener('click', this.handleClose));
    this.actionLinks.forEach((link) => link.addEventListener('click', this.handleActionLinkClick));

    this.modal.appendChild(panel);
    if (!this.modal.modal.querySelector('.m-promotion-popup__panel')) {
      this.modal.close();
      return;
    }

    const modalWidth = window.matchMedia('(max-width: 767px)').matches
      ? 'min(420px, calc(100vw - 24px))'
      : 'min(940px, calc(100vw - 32px))';
    this.modal.setWidth(modalWidth);
    this.markNextEligibleAt();
    this.modal.open();

    window.addEventListener('keydown', this.handleKeyDown);

    this.isOpen = true;
  }

  closePopup(remember = true) {
    if (!this.modal) return;

    clearTimeout(this.timer);

    this.modal.close();
    window.removeEventListener('keydown', this.handleKeyDown);

    this.isOpen = false;
  }

  handleOverlayClick(event) {
    if (event.target === this.modal?.modal || event.target.closest('.m-modal--close')) {
      this.closePopup(true);
    }
  }

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      this.closePopup(true);
    }
  }

  handleClose(event) {
    event.preventDefault();
    this.closePopup(false);
  }

  handleActionLinkClick() {
    this.closePopup(false);
  }

  handleSectionSelect(event) {
    if (event.detail.sectionId === this.dataset.sectionId && this.dataset.designMode === 'true') {
      this.openPopup();
    } else if (this.dataset.designMode === 'true') {
      this.closePopup(false);
    }
  }

  handleSectionLoad(event) {
    if (event.detail.sectionId === this.dataset.sectionId && this.dataset.designMode === 'true') {
      this.scheduleOpen(0);
    }
  }
}

if (!customElements.get('m-promotion-popup')) {
  customElements.define('m-promotion-popup', PromotionPopup);
}
